import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

interface LocalizationTable {
  name: string;
  title: string;
  route: string;
  recordCount?: number;
}

@Component({
  selector: 'app-localization-index',
  templateUrl: './localization-index.component.html',
  styleUrls: ['./localization-index.component.sass']
})
export class LocalizationIndexComponent implements OnInit {
  allTables: LocalizationTable[] = [];
  filteredTables: LocalizationTable[] = [];
  searchTerm: string = '';
  loading: boolean = false;

  constructor(
    private router: Router,
    private http: HttpClient
  ) {}

  async ngOnInit() {
    await this.loadTablesFromConfig();
    await this.loadTableCounts();
  }

  /**
   * Dynamically load table list from localization-config collection
   * This replaces the hardcoded table list
   */
  async loadTablesFromConfig() {
    this.loading = true;
    try {
      // Fetch active localization configurations from the API
      const configs = await firstValueFrom(
        this.http.get<Array<{ modelName: string; gridViewId: string }>>('/api/localization-configuration/active')
      );

      // Transform configs into LocalizationTable objects
      this.allTables = configs.map(config => ({
        name: config.modelName,
        title: this.formatTableName(config.modelName),
        route: `/localization/${config.modelName}`,
        recordCount: 0
      }));

      // NOTE: Find a better way to get all tables name from MySQL database directly instead of using the config collection
      // Add webadmin table (Gridly migration target)
      if (!this.allTables.some(t => t.name === 'webadmin')) {
        this.allTables.push({
          name: 'webadmin',
          title: 'Webadmin',
          route: '/localization/webadmin',
          recordCount: 0
        });
      }

      // Sort tables alphabetically by title
      this.allTables.sort((a, b) => a.title.localeCompare(b.title));

      this.filterTables();
    } catch (error) {
      console.error('Error loading localization tables from config:', error);
      // Fallback to empty array on error
      this.allTables = [];
      this.filteredTables = [];
    } finally {
      this.loading = false;
    }
  }

  /**
   * Format table name to title case with proper spacing
   * e.g., "nurture_collections" -> "Nurture Collections"
   */
  private formatTableName(name: string): string {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  async loadTableCounts() {
    try {
      // Get all table counts in a single optimized API call
      const counts = await firstValueFrom(
        this.http.get<Record<string, number>>('/api/localization/db/tables/counts')
      );

      // Update each table with its count from the batch response
      for (const table of this.allTables) {
        table.recordCount = counts[table.name] ?? 0;
      }
    } catch (error) {
      console.error('Error loading table counts:', error);
      // Set all counts to 0 on error
      for (const table of this.allTables) {
        table.recordCount = 0;
      }
    }
  }

  filterTables() {
    const term = this.searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredTables = [...this.allTables];
    } else {
      this.filteredTables = this.allTables.filter(table =>
        table.title.toLowerCase().includes(term) ||
        table.name.toLowerCase().includes(term)
      );
    }
  }

  navigateToTable(route: string) {
    this.router.navigateByUrl(route);
  }

  clearSearch() {
    this.searchTerm = '';
    this.filterTables();
  }
}

